import { slugifyAlias } from './accountAliases.ts'
import { encodeAccountHandleForPath } from './accountIdentity.ts'

/**
 * Deep links are always expressed as application-internal paths so that they can be
 * preserved across authentication and reused by emails, push notifications and the router.
 */
export function buildMessageDeepLinkPath(messageId: number): string {
  return `/messages/${messageId}`
}

/**
 * Every authenticated screen lives under the code of the account viewing it, which is what
 * lets two accounts be open in two tabs at once: each tab reads which account it belongs to
 * from its own URL rather than from shared state.
 *
 * Beside each code is the name that account gave — its own, and the counterpart's:
 *
 *     /abc-efg/fernando/xyz-123/mae
 *
 * **Only the codes are read.** The name segments are there so a person can tell one tab
 * from another and recognize a link before opening it, and nothing resolves them, which is
 * exactly what makes them safe to put there: a name may repeat, may hold accents and
 * spaces, and may be changed at any moment without breaking a link that already exists.
 * `/abc-efg/anything/xyz-123/anything` reaches the same screen.
 */
export function buildAccountScopedPath(
  accountId: string,
  accountAlias: string | null,
  applicationPath: string,
): string {
  return `/${accountId}/${slugifyAlias(accountAlias ?? accountId)}${applicationPath}`
}

export function buildConversationPath(
  counterpartAccountId: string,
  counterpartAlias: string | null,
): string {
  return `/${counterpartAccountId}/${slugifyAlias(counterpartAlias ?? counterpartAccountId)}`
}

export function buildThreadPath(
  counterpartAccountId: string,
  counterpartAlias: string | null,
  rootMessageId: number,
): string {
  return `${buildConversationPath(counterpartAccountId, counterpartAlias)}/threads/${rootMessageId}`
}

/**
 * Writing to somebody who has no account yet.
 *
 * A conversation URL names an account, and until the first message is sent there is no
 * account to name — writing to an address is what brings one into existence. So the path
 * that precedes it carries the address instead, behind a word reserved in every language
 * the product speaks, and becomes an ordinary conversation URL the moment the account
 * exists.
 */
export const INVITE_PATH_SEGMENTS: Readonly<Record<string, string>> = {
  'en-US': 'invite',
  'pt-BR': 'convidar',
  'es-419': 'invitar',
}

/** Every spelling is accepted whatever a deployment serves, because `intl` serves several. */
export const EVERY_INVITE_PATH_SEGMENT: readonly string[] = Object.values(INVITE_PATH_SEGMENTS)

export function buildInvitePath(locale: string, counterpartHandle: string): string {
  const inviteSegment = INVITE_PATH_SEGMENTS[locale] ?? INVITE_PATH_SEGMENTS['en-US']!
  return `/${inviteSegment}/${encodeAccountHandleForPath(counterpartHandle)}`
}

/**
 * The conversations somebody took out of their list.
 *
 * It is a screen of its own rather than a section of the list, because that is where people
 * already know to look for it. The word sits where a counterpart code would go, so it is
 * reserved in every language for the same reason the invite words are: a chosen name must
 * never be able to take it and make the screen unreachable.
 */
export const ARCHIVED_PATH_SEGMENTS: Readonly<Record<string, string>> = {
  'en-US': 'archived',
  'pt-BR': 'arquivadas',
  'es-419': 'archivadas',
}

/** Every spelling is accepted whatever a deployment serves, because `intl` serves several. */
export const EVERY_ARCHIVED_PATH_SEGMENT: readonly string[] = Object.values(ARCHIVED_PATH_SEGMENTS)

export function buildArchivedConversationsPath(locale: string): string {
  const archivedSegment = ARCHIVED_PATH_SEGMENTS[locale] ?? ARCHIVED_PATH_SEGMENTS['en-US']!
  return `/${archivedSegment}`
}

// Matching control characters is exactly the point: they must never enter a redirect.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/

/**
 * Guards against open redirects: only same-application absolute paths are ever preserved
 * through the authentication round trip.
 */
export function isSafeApplicationRedirectPath(candidatePath: string): boolean {
  if (typeof candidatePath !== 'string' || candidatePath.length === 0) {
    return false
  }
  if (candidatePath.length > 2048) {
    return false
  }
  if (!candidatePath.startsWith('/')) {
    return false
  }
  // Reject protocol relative URLs such as `//evil.example.com` and backslash variants.
  if (candidatePath.startsWith('//') || candidatePath.startsWith('/\\')) {
    return false
  }
  return !CONTROL_CHARACTER_PATTERN.test(candidatePath)
}

export function sanitizeApplicationRedirectPath(
  candidatePath: string | undefined | null,
  fallbackPath: string,
): string {
  if (typeof candidatePath === 'string' && isSafeApplicationRedirectPath(candidatePath)) {
    return candidatePath
  }
  return fallbackPath
}
